import {
  cidianAddress,
  readCidianHeadwords,
  readCidianIndex,
} from "../content/cidian/cidian-index-line.js";
import type { HskBand } from "../content/hsk/hsk-band.js";
import { DictionaryCache } from "./dictionary-cache.js";

/**
 * Which words the 词典 has a page for, answered without asking the network.
 *
 * A page that has segmented a text holds a few hundred words and no way of
 * telling which of them the site can define. 92.7% of the distinct words in
 * running text have an entry, so linking all of them sends about one word in
 * fourteen to a 404 and linking none of them withholds the definition from the
 * other thirteen. The index settles it before a link is drawn, and it carries
 * the HSK band beside each address, which is the other thing a row shows.
 *
 * **One fetch per device rather than per page.** The file is 128 KB, published
 * under a digest of its own contents and kept in the Cache Storage store the
 * dictionary already uses, so a later visit reads it off the disk it is on.
 *
 * **A device that will not have it still gets the tool.** A blocked store, a
 * failed fetch or a file that arrived truncated all leave an index holding
 * nothing, and a word it holds nothing about is drawn as plain text. That is
 * the page exactly as it was before any of this existed.
 */

/**
 * What every published index starts with, whichever digest it carries.
 *
 * Kept in step by hand with `src/assets/cidian-index.ts`, which writes the path
 * this is the fixed half of. The cache prunes on it, so that a reader holding
 * last week's index stops holding it once this one has arrived.
 */
export const publishedIndexes = "/data/cidian-";

/**
 * The 词典 addresses, and the band the entry at each one carries.
 *
 * Built from the published text, so an index holding nothing is
 * `new CidianIndex()` rather than a special case anybody has to handle.
 */
export class CidianIndex {
  readonly #addresses: ReadonlyMap<string, HskBand | undefined>;
  readonly #headwords: ReadonlyMap<string, string>;

  constructor(text = "") {
    this.#addresses = readCidianIndex(text);
    this.#headwords = readCidianHeadwords(text);
  }

  /** Where a word's entry is, or nothing where the section has no page. */
  addressOf(word: string): string | undefined {
    return this.#addresses.has(word) ? cidianAddress(word) : undefined;
  }

  /** The band a word's entry carries, where the entry is on the scale. */
  bandOf(word: string): HskBand | undefined {
    return this.#addresses.get(word);
  }

  /** The headword an address saves under, including for a redirect. */
  headwordOf(word: string): string | undefined {
    return this.#headwords.get(word);
  }
}

/** The index off this device where it is here, and over the wire where not. */
async function readIndex(
  url: string,
  cache: DictionaryCache,
  get: typeof globalThis.fetch,
): Promise<string | undefined> {
  const stored = await cache.match(url);
  if (stored !== undefined) {
    return stored.text();
  }

  const response = await get(url);
  if (!response.ok) {
    return undefined;
  }

  // Not awaited. The copy going to disk is not something the page is waiting
  // on, and `keep` swallows its own failures, so there is nothing here to wait
  // for and nothing to handle.
  void cache.keep(url, response.clone());

  return response.text();
}

/**
 * Fetch the index, or hand back one holding nothing.
 *
 * Nothing above this could do anything about a failure that this has not
 * already done. What a reader loses is the links and the bands, and what they
 * keep is the word list those were going to hang off.
 * @param url - The index, as a path on the site or an absolute address.
 * @param get - What fetches it, the browser's own unless a caller has another.
 */
export async function loadCidianIndex(
  url: string,
  get: typeof globalThis.fetch = globalThis.fetch.bind(globalThis),
): Promise<CidianIndex> {
  // The cache prunes by path, and the extension asks by absolute address.
  const current = url.startsWith("/") ? url : new URL(url).pathname;
  const cache = new DictionaryCache({ family: publishedIndexes, current });

  try {
    return new CidianIndex(await readIndex(url, cache, get));
  } catch {
    return new CidianIndex();
  }
}
